import { useMemo } from 'react';

import { type WorkspaceAgentThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/workspaceAgentThumbnailTypes';
import { usePrivateWorkspaceAgentPanelPillClips } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPillClipsProvider';
import { type FlattenedWorkspaceAgentAssetPillItem } from '~/components/v2/components/workspace/workspace-agent-panel/utils/flattenWorkspaceAgentAssetPillItems';
import { toWorkspaceAgentAssetListThumbnails } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentAssetListThumbnails';

export type UsePrivateWorkspaceAgentAssetListThumbnailsOptions = {
  /** While streaming, a member with no clip yet is pending, not unavailable. */
  isStreaming: boolean;
};

export type PrivateWorkspaceAgentAssetListThumbnails = {
  thumbnails: WorkspaceAgentThumbnail[];
  unavailableCount: number;
};

export const usePrivateWorkspaceAgentAssetListThumbnails = (
  items: FlattenedWorkspaceAgentAssetPillItem[],
  { isStreaming }: UsePrivateWorkspaceAgentAssetListThumbnailsOptions,
): PrivateWorkspaceAgentAssetListThumbnails => {
  const { getPillClipById, isClipLookupFailed, isPending, isRefreshing } = usePrivateWorkspaceAgentPanelPillClips();
  // `isRefreshing` too: a widened batch serves the previous, narrower one as placeholder data, so a
  // member it does not carry yet is still in flight rather than missing.
  const isResolving = isPending || isRefreshing || isStreaming;

  const thumbnails = useMemo(
    () =>
      toWorkspaceAgentAssetListThumbnails(
        items,
        (item) => (item.clipId ? getPillClipById(item.clipId) : undefined),
        isResolving,
        isClipLookupFailed,
      ),
    [getPillClipById, isClipLookupFailed, isResolving, items],
  );

  const unavailableCount = useMemo(
    () => thumbnails.filter((thumbnail) => thumbnail.isUnavailable).length,
    [thumbnails],
  );

  return { thumbnails, unavailableCount };
};
